import { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

const FORM_URL = 'https://readdy.ai/api/form/d6ve12dt4s7j0tv5iep0';

export default function ContactSection() {
  const { ref, inView } = useInView();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(false);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));

    try {
      setSubmitting(true);
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const contacts = [
    { icon: 'ri-map-pin-line', label: 'Endereço', value: 'Av. das Garças 30 - Vila Velha ES' },
    { icon: 'ri-phone-line', label: 'Telefone', value: '(27) 99516-4520' },
    { icon: 'ri-mail-line', label: 'E-mail', value: 'contatocomercialcep@gmail.com' },
    { icon: 'ri-time-line', label: 'Horário', value: 'Seg–Sex, 9h–18h' },
  ];

  return (
    <section id="contato" className="py-24 bg-[#F5F5F5]">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className={`text-center mb-16 ${inView ? 'animate-fade-in-up' : 'opacity-0-init'}`}>
          <p className="text-[#0F7C7A] text-sm font-semibold tracking-[0.2em] uppercase mb-3">
            Fale Conosco
          </p>
          <h2 className="font-display text-4xl lg:text-5xl text-[#1A1A1A] font-bold mb-4">
            Entre em <span className="italic text-[#0A5F5C]">Contato</span>
          </h2>
          <p className="text-[#555555] text-base max-w-lg mx-auto">
            Estamos prontas para ajudar você a encontrar os produtos perfeitos para sua rotina de beleza.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Info */}
          <div className={`${inView ? 'animate-fade-in-left' : 'opacity-0-init'}`}>
            <div className="relative rounded-3xl overflow-hidden mb-8 h-52">
              <img
                src="https://storage.readdy-site.link/project_files/bc073682-be8a-41b0-bc42-811cad36305d/ac8031b2-6409-4db5-b059-b8233c430362_fabrica-cosmeticos.webp?v=c9801628dd6149ad346db3be3d094c93"
                alt="Nossa fábrica"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F7C7A]/50 to-transparent flex items-end p-6">
                <p className="text-white font-display text-xl font-semibold">
                  Visite Nossa Fábrica
                </p>
              </div>
            </div>

            <div className="space-y-5">
              {contacts.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#E0F5F5] rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-[#0F7C7A] text-lg`} />
                  </div>
                  <div>
                    <p className="text-[#1A1A1A] font-semibold text-sm">{item.label}</p>
                    <p className="text-[#555555] text-sm mt-0.5">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <p className="text-[#2C2C2C] font-semibold text-sm mb-4">Siga-nos</p>
              <div className="flex gap-3">
                {[
                  { icon: 'ri-instagram-line', label: 'Instagram' },
                  { icon: 'ri-facebook-circle-line', label: 'Facebook' },
                  { icon: 'ri-tiktok-line', label: 'TikTok' },
                  { icon: 'ri-youtube-line', label: 'YouTube' },
                ].map((s) => (
                  <a
                    key={s.label}
                    href="#"
                    rel="nofollow"
                    className="w-10 h-10 flex items-center justify-center bg-[#E0F5F5] hover:bg-[#0F7C7A] text-[#0F7C7A] hover:text-white rounded-full transition-all duration-300 cursor-pointer"
                  >
                    <i className={`${s.icon} text-lg`} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className={`${inView ? 'animate-fade-in-right' : 'opacity-0-init'}`}>
            <div className="bg-white rounded-3xl p-8 border border-[#DFF0F0]">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#E0F5F5] rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-[#0F7C7A]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-[#2C2C2C] mb-2">Mensagem Enviada!</h3>
                  <p className="text-[#7A6860]">Entraremos em contato em até 24 horas.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-[#0F7C7A] text-sm font-semibold cursor-pointer hover:underline"
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#1A1A1A] text-sm font-medium mb-2">
                        Nome completo
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Seu nome"
                        className="w-full border border-[#C8E8E8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#AAAAAA] focus:outline-none focus:border-[#0F7C7A] bg-[#F5F5F5] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1A1A1A] text-sm font-medium mb-2">
                        E-mail
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="seu@email.com"
                        className="w-full border border-[#C8E8E8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#AAAAAA] focus:outline-none focus:border-[#0F7C7A] bg-[#F5F5F5] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#1A1A1A] text-sm font-medium mb-2">
                      Assunto
                    </label>
                    <select
                      name="assunto"
                      required
                      className="w-full border border-[#C8E8E8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#0F7C7A] bg-[#F5F5F5] transition-colors cursor-pointer"
                    >
                      <option value="">Selecione um assunto</option>
                      <option value="Dúvida sobre produto">Dúvida sobre produto</option>
                      <option value="Pedido e entrega">Pedido e entrega</option>
                      <option value="Parceria">Parceria</option>
                      <option value="Consultoria de beleza">Consultoria de beleza</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#1A1A1A] text-sm font-medium mb-2">
                      Mensagem
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Como podemos ajudar você?"
                      className="w-full border border-[#C8E8E8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#AAAAAA] focus:outline-none focus:border-[#0F7C7A] bg-[#F5F5F5] transition-colors resize-none"
                    />
                  </div>
                  {error && (
                    <p className="text-red-500 text-sm">
                      Ocorreu um erro. Tente novamente.
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0F7C7A] hover:bg-[#0A5F5C] disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        Enviar Mensagem
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
